import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles, CheckCircle2, AlertTriangle, BadgeAlert, RefreshCw, ListChecks,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (usd) => `$${(usd || 0).toFixed(2)}`;

const CONFIDENCE_BADGE = {
  high:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-cn-bone-100 text-cn-text-muted border-cn-border',
};

/**
 * CptSuggestionsPanel — drops into ClinicalNotes.js detail view.
 *
 * Reads cached suggestions on mount; user triggers a fresh LLM run via
 * the "Suggest CPT codes" button. Recommendations only — NEVER auto-bills.
 */
export const CptSuggestionsPanel = ({ noteId, getAuthHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadCached = useCallback(async () => {
    if (!noteId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clinical-notes/${noteId}/cpt-suggestions`, getAuthHeader());
      if (data?.cached) setData(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [noteId, getAuthHeader]);

  useEffect(() => { loadCached(); }, [loadCached]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/clinical-notes/${noteId}/suggest-cpt`,
        {},
        { ...getAuthHeader(), timeout: 75000 },
      );
      setData(data);
      toast.success('CPT suggestions generated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion failed');
    } finally {
      setGenerating(false);
    }
  };

  const totalRvu = (data?.suggestions || []).reduce((s, x) => s + (x.total_rvu || 0), 0);
  const topReimbursement = (data?.suggestions || [])[0]?.medicare_reimbursement_usd || 0;

  return (
    <section className="cn-card p-5 mt-4" data-testid="cpt-suggestions-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="cn-eyebrow mb-1 inline-flex items-center gap-1.5">
            <ListChecks className="w-3.5 h-3.5" /> AI Coding
          </div>
          <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">
            CPT + RVU suggestions
          </h3>
        </div>
        <Button onClick={generate} disabled={generating} className="cn-btn cn-btn-primary !py-2" data-testid="cpt-generate">
          {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Coding (10-15s)…' : data ? 'Regenerate' : 'Suggest CPT codes'}
        </Button>
      </div>

      {loading && !data && <div className="h-24 animate-pulse bg-cn-bone-100/40 rounded-lg" />}

      {!loading && !data && (
        <div className="text-sm text-cn-text-muted py-4 px-2" data-testid="cpt-empty">
          Click <strong>Suggest CPT codes</strong>. Recommendations only — you remain in control of submission.
        </div>
      )}

      {data && (
        <>
          {/* Status pills */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {data.ai_unavailable ? (
              <span className="cn-trust-badge text-amber-700 border-amber-200 bg-amber-50">
                <BadgeAlert className="w-3 h-3" /> Heuristic fallback
              </span>
            ) : (
              <span className="cn-trust-badge text-emerald-700 border-emerald-200 bg-emerald-50">
                <CheckCircle2 className="w-3 h-3" /> LLM-coded
              </span>
            )}
            <span className="cn-trust-badge">Total RVU: {totalRvu.toFixed(2)}</span>
            <span className="cn-trust-badge">Top: {money(topReimbursement)}/visit</span>
          </div>

          {/* Suggestions */}
          <ul className="space-y-2.5" data-testid="cpt-list">
            {(data.suggestions || []).map((s, i) => (
              <li key={i} className="rounded-xl border border-cn-border p-4" data-testid={`cpt-row-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-cn-primary" data-testid={`cpt-code-${i}`}>{s.code}</div>
                    <div className="text-sm text-cn-text font-semibold">{s.name}</div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${CONFIDENCE_BADGE[s.confidence] || CONFIDENCE_BADGE.medium}`}
                    data-testid={`cpt-confidence-${i}`}
                  >
                    {(s.confidence || 'medium').toUpperCase()}
                  </span>
                </div>
                {s.rationale && (
                  <p className="text-xs text-cn-text-muted leading-relaxed mb-2 italic" data-testid={`cpt-rationale-${i}`}>
                    &ldquo;{s.rationale}&rdquo;
                  </p>
                )}
                <div className="flex items-center gap-4 flex-wrap text-xs">
                  <span className="text-cn-text-muted">Work RVU: <strong className="text-cn-text">{s.work_rvu}</strong></span>
                  <span className="text-cn-text-muted">Total RVU: <strong className="text-cn-text">{s.total_rvu}</strong></span>
                  <span className="text-cn-text-muted">Medicare est.: <strong className="text-cn-primary">{money(s.medicare_reimbursement_usd)}</strong></span>
                  {s.suggested_modifiers?.length > 0 && (
                    <span className="text-cn-text-muted">
                      Modifiers:{' '}
                      {s.suggested_modifiers.map((m, mi) => (
                        <span key={mi} className="inline-block px-1.5 py-0.5 mx-0.5 rounded bg-cn-bone-100 font-mono text-[10px]">{m}</span>
                      ))}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/30 p-3 flex items-start gap-2" data-testid="cpt-disclaimer">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
            <p className="text-xs text-cn-text-muted leading-relaxed">
              <strong>Recommendation only.</strong> AI may misread context. You remain responsible
              for final code selection — the engine NEVER auto-bills. Conversion factor: $32.74/RVU
              (Medicare 2026).
            </p>
          </div>
        </>
      )}
    </section>
  );
};

export default CptSuggestionsPanel;
